import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { fitPresentationScale } from "./fitPresentationScale";

export function PresentationFitStage(props: {
  readonly designWidth: number;
  readonly contentKey: string;
  readonly className?: string;
  readonly children: ReactNode;
}) {
  const { designWidth, contentKey, className, children } = props;
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [contentHeight, setContentHeight] = useState(0);

  const update = useCallback(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;
    const nextHeight = content.scrollHeight;
    const nextScale = fitPresentationScale({
      availableWidth: viewport.clientWidth,
      availableHeight: viewport.clientHeight,
      contentWidth: designWidth,
      contentHeight: nextHeight,
    });
    setContentHeight(nextHeight);
    setScale(nextScale);
  }, [designWidth]);

  useLayoutEffect(() => {
    update();
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => update());
    observer.observe(viewport);
    observer.observe(content);
    return () => observer.disconnect();
  }, [contentKey, update]);

  return (
    <div
      ref={viewportRef}
      className={["presentation-fit-viewport", className ?? ""].filter(Boolean).join(" ")}
    >
      <div
        className="presentation-fit-sizer"
        style={{
          width: designWidth * scale,
          height: Math.max(contentHeight * scale, 1),
        }}
      >
        <div
          ref={contentRef}
          className="presentation-fit-content"
          style={{ width: designWidth, transform: `scale(${scale})` }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
